import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../lib/api';
import { sessionQueryKey } from '../../../../lib/session';

/**
 * Modules of the organization (docs/dev/api-organization.md „Modules (Admin 05)"). Query root `['organization']`
 * (realtime `organization.updated { section: 'modules' }`); a toggle refetches the session (tools, `modules`).
 */
export interface ModuleCard {
  key: string;
  name: string;
  description: string;
  note: string | null;
  active: boolean;
  actions: { toggle: { allowed: boolean; reason: string | null } };
}

export interface ModulesResponse {
  intro: string;
  items: ModuleCard[];
}

export const modulesKey = ['organization', 'modules'] as const;

export function useModules() {
  return useQuery({
    queryKey: modulesKey,
    queryFn: () => api<ModulesResponse>('/settings/organization/modules'),
    retry: false,
  });
}

export function useToggleModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, active }: { key: string; active: boolean }) =>
      api<ModulesResponse>(`/settings/organization/modules/${key}`, { method: 'PATCH', body: { active } }),
    onSuccess: (data) => {
      queryClient.setQueryData(modulesKey, data);
      void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}
